import $$api from 'models/api';
import { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import styled from 'styled-components';

import Dropdown from 'components/StyledTemplates/Dropdown';
import { Frame, Row, RowWrapper } from 'components/StyledTemplates/StyledTemplates';
import { Option } from 'components/Table/Table';

import DashboardService from 'services/DashboardService';
import DocumentDownloadService from 'services/DocumentDownloadService';

import useResources from 'hooks/useResources';

import imgIconsDownloadDocuments from 'assets/icons/download-documents.png';
import imgThreeDotsVert from 'assets/icons/more-vert-dark.svg';

import { Document } from 'types/zod/document';

interface GiftTrustDocumentCardProps {
    document: Document;
}

const GiftTrustDocumentCard = (props: GiftTrustDocumentCardProps) => {
    const { document } = props;
    const isSupporting = document.category_code === 'gift_trust_supporting';

    const { t } = useResources();
    const [isUploading, setUploading] = useState(false);

    const handlers = {
        download: async () => {
            await DocumentDownloadService.downloadPdf(document.document_id);
        },
        upload: () => {
            const input = window.document.createElement('input');
            input.type = 'file';
            input.onchange = async (e: any) => {
                const file = e.target.files?.[0];
                try {
                    setUploading(true);
                    await DashboardService.uploadSignedDocument(document.document_id, file);
                } finally {
                    setUploading(false);
                }
                await $$api.participants.getCommunityFx(undefined);
            };
            input.click();
        },
        downloadScan: async (filename: string) => {
            await $$api.documents.downloadScanFx({ documentId: document.document_id, filename });
        },
        deleteScan: async (filename: string) => {
            await $$api.documents.deleteScanFx({ id: document.document_id, filename });
            await $$api.participants.getCommunityFx(undefined);
        },
    };

    return (
        <div className="document-card">
            <div className="name-row">
                <div>{t(`documentType.${document.document_type_code}`)}</div>
                <Dropdown
                    toggle={<img alt="Three dots" src={imgThreeDotsVert} />}
                    menu={
                        <>
                            <Option onClick={() => handlers.download()}>
                                {t('giftTrustPage.giftTrustDocumentsModal.download')}
                            </Option>
                            {!isSupporting && (
                                <Option
                                    onClick={() => {
                                        handlers.upload();
                                    }}
                                >
                                    {t('giftTrustPage.giftTrustDocumentsModal.uploadSignedDocuments')}
                                </Option>
                            )}
                        </>
                    }
                />
            </div>
            {(document?.scan_names || []).length > 0 && (
                <>
                    <div className="uploaded-docs-header">
                        {t('giftTrustPage.giftTrustDocumentsModal.uploadedScans')}
                    </div>
                    <Frame extra="width: 100%; > * { margin-top: 10px; };">
                        {document?.scan_names?.map?.((scanName: string, index: number) => (
                            <RowWrapper key={index}>
                                <Row
                                    data-qaid="download_scan_button"
                                    className="pointer"
                                    onClick={async () => {
                                        await handlers.downloadScan(scanName);
                                    }}
                                >
                                    <DownloadIcon />
                                    <Button data-qaid="download_scan_button" variant="plain">
                                        {scanName}
                                    </Button>
                                </Row>
                                <Button
                                    data-qaid="delete_scan_button"
                                    onClick={async () => {
                                        await handlers.deleteScan(scanName);
                                    }}
                                    variant="danger"
                                >
                                    {t(`newDashboardPage.scansModal.delete`)}
                                </Button>
                            </RowWrapper>
                        ))}
                    </Frame>
                </>
            )}
            {isUploading && (
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="mt-2" />
            )}
        </div>
    );
};

const DownloadIcon = styled(Frame)`
    width: 14px;
    height: 14px;
    margin-right: 8px;
    background: url('${imgIconsDownloadDocuments}') no-repeat center center / contain;
`;

export default GiftTrustDocumentCard;
